import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronRight, Zap, Heart, Wind, Circle, Compass, MessageCircle, Users, Brain, ClipboardCheck, RefreshCw } from 'lucide-react';
import { useFavorites, TOOL_METADATA } from '../hooks/useFavorites';
import { SmallText } from './brand/BrandTypography';

/**
 * QuickAccessFavorites - One-tap access to user's favorite tools
 * 
 * Shows the user's top 3 favorited/most-used tools at the top of the dashboard
 * for fastest possible access when they need relief quickly.
 */

// Icon mapping
const ICON_MAP = {
    Heart: Heart,
    Wind: Wind,
    Circle: Circle,
    Compass: Compass,
    Zap: Zap,
    MessageCircle: MessageCircle,
    Users: Users,
    Brain: Brain,
    ClipboardCheck: ClipboardCheck,
    RefreshCw: RefreshCw,
    Sparkles: Zap,
    Star: Star
};

// Get most used tools from usage history
const getMostUsedTools = () => {
    try {
        const usageHistory = JSON.parse(localStorage.getItem('coregulate_tool_usage') || '[]');
        
        // Count usage per tool
        const usageCount = {};
        usageHistory.forEach(entry => {
            const path = entry.path || entry.tool_path;
            if (path) {
                usageCount[path] = (usageCount[path] || 0) + 1;
            }
        });
        
        // Sort by usage count and return top 3
        return Object.entries(usageCount)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([path]) => path);
    } catch {
        return [];
    }
};

// Variant styles - 4 color palette (Teal, Coral, Dusty Rose, Neutral)
const variantStyles = {
    sage: {
        bg: 'bg-gradient-to-br from-[#2A8585]/15 to-[#1D6B6B]/10',
        iconBg: 'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]',
        border: 'border-[#2A8585]/30'
    },
    lavender: {
        bg: 'bg-gradient-to-br from-[#E8A0A0]/15 to-[#C97B7B]/10',
        iconBg: 'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]',
        border: 'border-[#E8A0A0]/30'
    },
    rose: {
        bg: 'bg-gradient-to-br from-[#FFBB98]/15 to-[#E09060]/10',
        iconBg: 'bg-gradient-to-br from-[#FFBB98] to-[#E09060]',
        border: 'border-[#FFBB98]/30'
    },
    blue: {
        bg: 'bg-gradient-to-br from-[#2A8585]/15 to-[#1D6B6B]/10',
        iconBg: 'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]',
        border: 'border-[#2A8585]/30'
    },
    gold: {
        bg: 'bg-gradient-to-br from-[#FFBB98]/15 to-[#E09060]/10',
        iconBg: 'bg-gradient-to-br from-[#FFBB98] to-[#E09060]',
        border: 'border-[#FFBB98]/30'
    }
};

export const QuickAccessFavorites = () => {
    const navigate = useNavigate();
    const { favorites, getFavoritesWithMetadata } = useFavorites();
    
    // Get favorites with metadata
    const favoritesWithMeta = getFavoritesWithMetadata();
    
    // Get most used tools as fallback
    const mostUsed = getMostUsedTools();
    
    // Combine: prioritize favorites, then fill with most used
    let quickAccessTools = [...favoritesWithMeta.slice(0, 3)];
    
    // If we don't have 3 favorites, add from most used
    if (quickAccessTools.length < 3) {
        const existingPaths = quickAccessTools.map(t => t.path);
        mostUsed.forEach(path => {
            if (quickAccessTools.length < 3 && !existingPaths.includes(path) && TOOL_METADATA[path]) {
                quickAccessTools.push({
                    path,
                    ...TOOL_METADATA[path]
                });
            }
        });
    }
    
    // If still no tools, show default suggestions
    if (quickAccessTools.length === 0) {
        quickAccessTools = [
            { path: '/regulate/micro-reset', ...TOOL_METADATA['/regulate/micro-reset'] },
            { path: '/regulate/breathing-orb', ...TOOL_METADATA['/regulate/breathing-orb'] },
            { path: '/regulate/still-sphere', ...TOOL_METADATA['/regulate/still-sphere'] }
        ];
    }

    const handleToolClick = (path) => {
        // Track usage
        try {
            const usageHistory = JSON.parse(localStorage.getItem('coregulate_tool_usage') || '[]');
            usageHistory.push({
                path,
                timestamp: new Date().toISOString(),
                source: 'quick_access'
            });
            // Keep last 100 entries
            if (usageHistory.length > 100) {
                usageHistory.shift();
            }
            localStorage.setItem('coregulate_tool_usage', JSON.stringify(usageHistory));
        } catch (e) {
            console.error('Failed to track usage:', e);
        }
        
        navigate(path);
    };

    return (
        <section className="mb-6" data-testid="quick-access-favorites">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#FFBB98]" />
                    <SmallText className="text-[#7D8E95] font-medium uppercase tracking-wide text-xs">
                        Quick Access
                    </SmallText>
                </div>
                {favorites.length > 0 && (
                    <button 
                        onClick={() => navigate('/favorites')}
                        className="text-xs text-[#7D8E95] hover:text-[#1D6B6B] flex items-center gap-1 transition-colors"
                    >
                        All favorites
                        <ChevronRight className="w-3 h-3" />
                    </button>
                )}
            </div>

            {/* Quick Access Cards - Icon Only Design */}
            <div className="flex justify-around gap-2">
                {quickAccessTools.slice(0, 3).map((tool, index) => {
                    const IconComponent = ICON_MAP[tool.icon] || Heart;
                    const style = variantStyles[tool.variant] || variantStyles.sage;
                    
                    return (
                        <button
                            key={tool.path}
                            onClick={() => handleToolClick(tool.path)}
                            className="relative flex flex-col items-center gap-1.5 p-1 transition-all duration-200 hover:scale-105 active:scale-95"
                            data-testid={`quick-access-tool-${index}`}
                        >
                            {/* Favorite Star Badge */}
                            {favorites.includes(tool.path) && (
                                <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#FFBB98] flex items-center justify-center shadow-sm z-10">
                                    <Star className="w-2.5 h-2.5 text-white fill-white" />
                                </div>
                            )}
                            
                            {/* Icon */}
                            <div className={`w-11 h-11 rounded-xl ${style.iconBg} flex items-center justify-center shadow-md`}>
                                <IconComponent className="w-5 h-5 text-white" />
                            </div>
                            
                            {/* Title */}
                            <p className="text-[10px] font-medium text-[#1D6B6B] text-center leading-tight max-w-[70px]">
                                {tool.title}
                            </p>
                        </button>
                    );
                })}
            </div>

            {/* Hint for new users */}
            {favorites.length === 0 && (
                <p className="text-xs text-[#7D8E95] text-center mt-3 italic">
                    Tap the star on any tool to add it to Quick Access
                </p>
            )}
        </section>
    );
};

export default QuickAccessFavorites;
